import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, ArrowRight, MessageSquare } from 'lucide-react';
import demos from '../../data/demos';
import useSeo from '../../hooks/useSeo';

function Demos() {
  useSeo({
    title: 'Demo Gallery — See What I Build | Sudco Solutions',
    description:
      'Live demo websites for real small businesses — bakeries, dentists, plumbers, florists, gyms, and more. See the quality before you pay. $800 flat.',
    path: '/demos',
  });

  const industries = useMemo(
    () => ['All', ...Array.from(new Set(demos.map((d) => d.industry)))],
    []
  );
  const [filter, setFilter] = useState('All');
  const visible = filter === 'All' ? demos : demos.filter((d) => d.industry === filter);

  return (
    <div className="min-h-screen bg-bg text-text font-sans">
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-accent/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <p className="text-sm text-accent-2 font-medium mb-4 uppercase tracking-wider">Sample sites</p>
            <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tight text-text mb-5 leading-tight">
              Real industries. Real quality.{' '}
              <span className="gradient-text">Zero commitment.</span>
            </h1>
            <p className="text-lg md:text-xl text-text-muted max-w-2xl mx-auto">
              Pick an industry that looks like yours and walk through it. This is the level of polish and detail you'd get — custom-built for your actual business.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-10">
            {industries.map((tag) => (
              <button
                key={tag}
                onClick={() => setFilter(tag)}
                className={`px-4 py-1.5 rounded-full text-sm border transition-all ${
                  filter === tag
                    ? 'bg-gradient-brand text-white border-transparent shadow-glow'
                    : 'glass text-text-muted hover:text-text hover:border-accent/40'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: (i % 3) * 0.05 }}
              >
                <Link
                  to={`/demos/${d.slug}`}
                  className="group block glass rounded-2xl overflow-hidden hover:border-accent/40 transition-all hover:-translate-y-1 duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-surface-2">
                    <img
                      src={d.cover}
                      alt={d.name}
                      width="1600"
                      height="900"
                      loading={i < 3 ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchpriority={i < 3 ? 'high' : 'low'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg/90 via-bg/20 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="glass px-3 py-1 rounded-full text-xs text-text">
                        {d.industry}
                      </span>
                      <span className="bg-bg/80 border border-border px-2 py-1 rounded-full text-[10px] uppercase tracking-wider text-text-muted">
                        Sample
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gradient-brand flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight size={16} className="text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="font-display font-semibold text-lg text-text mb-1">{d.name}</h2>
                    <p className="text-sm text-text-muted mb-3 line-clamp-2">{d.tagline}</p>
                    <div className="flex items-center gap-1.5 text-xs text-text-muted">
                      <MapPin size={12} />
                      <span>{d.location}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: visible.length * 0.05 }}
              className="border border-border border-dashed rounded-2xl bg-transparent p-8 flex flex-col justify-between min-h-[280px]"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-surface-2 border border-border flex items-center justify-center mb-5">
                  <MessageSquare size={18} className="text-accent-2" />
                </div>
                <h2 className="font-display font-semibold text-xl text-text mb-2">Don't see your industry?</h2>
                <p className="text-text-muted text-sm leading-relaxed">
                  Tell me what you do and I'll build a free demo for your specific business — no commitment required.
                </p>
              </div>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 text-sm text-accent-2 font-medium hover:text-text transition-colors mt-6"
              >
                Request a custom demo <ArrowRight size={14} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Demos;
